import {
  AlarmClock,
  BadgeCheck,
  Banknote,
  CalendarClock,
  CircleX,
  ClipboardList,
  ClipboardPlus,
  Flame,
  HandCoins,
  PackageCheck,
  Scissors,
  Shirt,
  Sparkles,
  Store,
  Truck,
  Wallet,
  WandSparkles,
  type LucideIcon,
} from 'lucide-react';
import type { WorkflowStage } from '@tailoring-crm/shared';

/**
 * What the Home dashboard paints each thing with, decided once.
 *
 * A colour only means something if it means the same thing everywhere it appears: if "ready" is
 * green on a tile, it must be green on the stage bar and the board badge too, or the owner stops
 * reading colour at all and goes back to reading labels. So the icon and tone for every tile,
 * every stage and every person live here, in one table each, and the components only look them
 * up. Nothing on the dashboard picks a colour inline.
 */

/** The ten hues of `--tone-*` in `index.css`. */
export type Tone =
  'blue' | 'green' | 'amber' | 'red' | 'violet' | 'teal' | 'slate' | 'indigo' | 'pink' | 'orange';

export interface ToneClasses {
  /** A faint wash and border for a whole surface — a tile, a row. */
  readonly tint: string;
  /** A solid swatch that carries a white icon. */
  readonly chip: string;
  /** A solid fill with no content of its own — a progress bar, a stage segment. */
  readonly bar: string;
  /** The tone written as text, for a figure or a label that should carry it. */
  readonly text: string;
  /** The tone as an SVG fill — a chart's bar or area. */
  readonly fill: string;
  /** The tone as an SVG stroke — a chart's line or a donut's segment. */
  readonly stroke: string;
}

/**
 * Every class a tone can produce, spelled out in full.
 *
 * Tailwind's JIT finds classes by scanning source text for complete strings; it never runs the
 * code. A class assembled as `` `bg-tone-${tone}` `` is invisible to it and simply never gets
 * generated — the tile renders uncoloured and nothing fails. Writing all sixty strings by hand
 * is the price of every one of them actually existing in the stylesheet.
 */
export const TONE_CLASSES: Readonly<Record<Tone, ToneClasses>> = Object.freeze({
  blue: {
    tint: 'bg-tone-blue/10 border-tone-blue/20',
    chip: 'bg-tone-blue text-white',
    bar: 'bg-tone-blue',
    text: 'text-tone-blue',
    fill: 'fill-tone-blue',
    stroke: 'stroke-tone-blue',
  },
  green: {
    tint: 'bg-tone-green/10 border-tone-green/20',
    chip: 'bg-tone-green text-white',
    bar: 'bg-tone-green',
    text: 'text-tone-green',
    fill: 'fill-tone-green',
    stroke: 'stroke-tone-green',
  },
  amber: {
    tint: 'bg-tone-amber/10 border-tone-amber/20',
    chip: 'bg-tone-amber text-white',
    bar: 'bg-tone-amber',
    text: 'text-tone-amber',
    fill: 'fill-tone-amber',
    stroke: 'stroke-tone-amber',
  },
  red: {
    tint: 'bg-tone-red/10 border-tone-red/20',
    chip: 'bg-tone-red text-white',
    bar: 'bg-tone-red',
    text: 'text-tone-red',
    fill: 'fill-tone-red',
    stroke: 'stroke-tone-red',
  },
  violet: {
    tint: 'bg-tone-violet/10 border-tone-violet/20',
    chip: 'bg-tone-violet text-white',
    bar: 'bg-tone-violet',
    text: 'text-tone-violet',
    fill: 'fill-tone-violet',
    stroke: 'stroke-tone-violet',
  },
  teal: {
    tint: 'bg-tone-teal/10 border-tone-teal/20',
    chip: 'bg-tone-teal text-white',
    bar: 'bg-tone-teal',
    text: 'text-tone-teal',
    fill: 'fill-tone-teal',
    stroke: 'stroke-tone-teal',
  },
  slate: {
    tint: 'bg-tone-slate/10 border-tone-slate/20',
    chip: 'bg-tone-slate text-white',
    bar: 'bg-tone-slate',
    text: 'text-tone-slate',
    fill: 'fill-tone-slate',
    stroke: 'stroke-tone-slate',
  },
  indigo: {
    tint: 'bg-tone-indigo/10 border-tone-indigo/20',
    chip: 'bg-tone-indigo text-white',
    bar: 'bg-tone-indigo',
    text: 'text-tone-indigo',
    fill: 'fill-tone-indigo',
    stroke: 'stroke-tone-indigo',
  },
  pink: {
    tint: 'bg-tone-pink/10 border-tone-pink/20',
    chip: 'bg-tone-pink text-white',
    bar: 'bg-tone-pink',
    text: 'text-tone-pink',
    fill: 'fill-tone-pink',
    stroke: 'stroke-tone-pink',
  },
  orange: {
    tint: 'bg-tone-orange/10 border-tone-orange/20',
    chip: 'bg-tone-orange text-white',
    bar: 'bg-tone-orange',
    text: 'text-tone-orange',
    fill: 'fill-tone-orange',
    stroke: 'stroke-tone-orange',
  },
});

export interface Visual {
  readonly icon: LucideIcon;
  readonly tone: Tone;
}

/**
 * The keys of `DASHBOARD_TILES`.
 *
 * Spelled out here rather than derived, because the shared package declares a tile's `key` as a
 * plain `string` — deriving from it would give `Record<string, …>`, and a tile added there would
 * then compile here without a visual. As a closed union, a missing entry in `TILE_VISUALS` is a
 * type error, and `visuals.test.ts` checks the union against the live `DASHBOARD_TILES` list so
 * the two cannot drift apart unnoticed.
 */
export type DashboardTileKey =
  | 'dueToday'
  | 'overdue'
  | 'ready'
  | 'inProgress'
  | 'outstanding'
  | 'collectedToday'
  | 'bookedToday'
  | 'unsettledWages';

/**
 * Each morning figure's icon and colour.
 *
 * `overdue` is red here because red is what it means when there is something overdue; the tile
 * swaps it for slate when the count is zero, since a red tile reading "0" is an alarm about
 * nothing. Money tiles take violet and teal so they never borrow the green of "ready" — money
 * collected is good news, but it is not the same good news as a garment waiting on the rail.
 */
export const TILE_VISUALS: Readonly<Record<DashboardTileKey, Visual>> = Object.freeze({
  dueToday: { icon: CalendarClock, tone: 'blue' },
  overdue: { icon: AlarmClock, tone: 'red' },
  ready: { icon: PackageCheck, tone: 'green' },
  inProgress: { icon: Scissors, tone: 'amber' },
  outstanding: { icon: Wallet, tone: 'violet' },
  collectedToday: { icon: HandCoins, tone: 'teal' },
  bookedToday: { icon: ClipboardPlus, tone: 'blue' },
  unsettledWages: { icon: Banknote, tone: 'violet' },
});

/**
 * Each workflow stage's icon and colour, as a ramp.
 *
 * The tones walk round the colour wheel in the order a garment moves through the shop — slate
 * while it is still paperwork, cool blues through the cutting table and the machines, warming
 * through handwork, finishing and the iron, amber at the QC check, and green when it is on the
 * rail. A glance at a row of stage chips therefore says roughly how far along the work is before
 * any label is read. `delivered` steps off the ramp to teal (done, and out of the shop), and
 * `cancelled` is the only red, because it is the only stage that is a loss.
 */
export const STAGE_VISUALS: Readonly<Record<WorkflowStage, Visual>> = Object.freeze({
  booked: { icon: ClipboardList, tone: 'slate' },
  showroom: { icon: Store, tone: 'slate' },
  cutting: { icon: Scissors, tone: 'blue' },
  stitching: { icon: Shirt, tone: 'indigo' },
  handwork: { icon: Sparkles, tone: 'violet' },
  finishing: { icon: WandSparkles, tone: 'pink' },
  ironing: { icon: Flame, tone: 'orange' },
  qc: { icon: BadgeCheck, tone: 'amber' },
  ready: { icon: PackageCheck, tone: 'green' },
  delivered: { icon: Truck, tone: 'teal' },
  cancelled: { icon: CircleX, tone: 'red' },
});

/**
 * The tones a person may be given. Red and slate are left out on purpose: next to a name, red
 * reads as "this person is a problem" and slate as "this person is switched off", and nobody on
 * the staff list should look like either just because of how their ID happens to hash.
 */
export const STAFF_TONES: readonly Tone[] = Object.freeze([
  'blue',
  'green',
  'violet',
  'teal',
  'indigo',
  'pink',
] as const);

/**
 * A stable colour for a member of staff.
 *
 * Hashed from the ID rather than assigned by position, so Ravi is the same colour on every
 * screen and every day — sorting the list, hiring someone or a colleague leaving never repaints
 * him. FNV-1a is used because it is a few lines, needs no dependency, and spreads short,
 * similar strings (UUIDs that share a prefix) across the palette rather than bunching them.
 */
export function staffTone(staffId: string): Tone {
  let hash = 0x811c9dc5;
  for (let i = 0; i < staffId.length; i += 1) {
    hash ^= staffId.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  // `>>> 0` reads the 32-bit result as unsigned, so the remainder is never negative.
  return STAFF_TONES[(hash >>> 0) % STAFF_TONES.length]!;
}

/**
 * The colour of booked sales on the Home charts — the monthly bars and this month's trend line.
 *
 * Blue because the sales report counts what was BOOKED, and "Booked today" is the blue tile;
 * the money tiles' violet and teal mean owed and collected, which are different sums, and a
 * chart that borrowed their colour would be read as one of them.
 */
export const SALES_TONE: Tone = 'blue';
